import { useCallback, useEffect, useRef } from 'react'
import type { StickerPlacement } from './bakeStickers'

interface Point {
  x: number
  y: number
}

/** A finger that took part in a pinch lifts; its click must not land underneath. */
const CLICK_SUPPRESS_MS = 400

export interface StickerGestureHandlers {
  /** A sticker was picked up (id) or the gesture ended on nothing (null). */
  onSelect: (id: string | null) => void
  /** Current state of a placement; read on every move so it is never stale. */
  get: (id: string) => StickerPlacement | undefined
  move: (id: string, xPct: number, yPct: number) => void
  /** Pinch: absolute width (as a % of strip width) and rotation in degrees. */
  transform: (id: string, widthPct: number, rotation: number) => void
  /** A gesture is about to change a sticker — the moment to save an undo step. */
  onGestureStart?: () => void
}

/**
 * Drag, pinch and rotate for a placed sticker.
 *
 * Once a finger lands on a sticker, the gesture owns EVERY pointer on the page
 * until the last finger lifts. It listens on `window` in the capture phase, so
 * a second finger is caught before whatever it happens to land on — the strip,
 * the background, the sticker tray — can react to it. With per-element
 * handlers the second finger went to the element under it, which dropped the
 * sticker on the background and added a new one on the tray.
 *
 * One finger moves the sticker, keeping the grip where it started rather than
 * snapping the sticker's centre to the finger. Two fingers scale and rotate
 * it, measured against where the pinch began so it cannot drift as the fingers
 * wander.
 *
 * Coordinates are percentages of the strip box, which is how placements are
 * stored: the same numbers mean the same thing here and in the print bake.
 */
export function useStickerGestures(
  stripRef: React.RefObject<HTMLDivElement | null>,
  handlers: StickerGestureHandlers
): {
  /** From a sticker's onPointerDown: start (or join) the gesture on it. */
  begin: (e: React.PointerEvent, id: string) => void
} {
  // Handlers change identity every render; the window listeners read the latest.
  const h = useRef(handlers)
  h.current = handlers

  const pointers = useRef(new Map<number, Point>())
  const activeId = useRef<string | null>(null)
  const pinch = useRef<{ dist: number; angle: number; widthPct: number; rotation: number } | null>(
    null
  )
  /** Offset from the dragging finger to the sticker's centre, in strip %. */
  const grip = useRef<Point | null>(null)
  const suppressClicksUntil = useRef(0)
  const listening = useRef(false)

  const toPct = useCallback(
    (p: Point): Point | null => {
      const box = stripRef.current?.getBoundingClientRect()
      if (!box?.width || !box.height) return null
      return {
        x: ((p.x - box.left) / box.width) * 100,
        y: ((p.y - box.top) / box.height) * 100
      }
    },
    [stripRef]
  )

  const current = (): StickerPlacement | undefined =>
    activeId.current ? h.current.get(activeId.current) : undefined

  /** Re-anchor a one-finger drag on whichever finger is left. */
  const regrip = useCallback((): void => {
    const [finger] = [...pointers.current.values()]
    const placement = current()
    const at = finger ? toPct(finger) : null
    grip.current = at && placement ? { x: placement.xPct - at.x, y: placement.yPct - at.y } : null
  }, [toPct])

  const startPinch = useCallback((): void => {
    const [a, b] = [...pointers.current.values()]
    const placement = current()
    if (!a || !b || !placement) return
    pinch.current = {
      dist: Math.hypot(b.x - a.x, b.y - a.y),
      angle: (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI,
      widthPct: placement.widthPct,
      rotation: placement.rotation
    }
  }, [])

  const onMove = useCallback(
    (e: PointerEvent): void => {
      const id = activeId.current
      if (!id || !pointers.current.has(e.pointerId)) return
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })

      if (pointers.current.size >= 2 && pinch.current) {
        const [a, b] = [...pointers.current.values()]
        const dist = Math.hypot(b.x - a.x, b.y - a.y)
        const angle = (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI
        const scale = pinch.current.dist > 0 ? dist / pinch.current.dist : 1
        h.current.transform(
          id,
          pinch.current.widthPct * scale,
          pinch.current.rotation + (angle - pinch.current.angle)
        )
        return
      }

      const at = toPct({ x: e.clientX, y: e.clientY })
      if (!at || !grip.current) return
      h.current.move(
        id,
        Math.min(100, Math.max(0, at.x + grip.current.x)),
        Math.min(100, Math.max(0, at.y + grip.current.y))
      )
    },
    [toPct]
  )

  const onDown = useCallback(
    (e: PointerEvent): void => {
      if (!activeId.current || pointers.current.has(e.pointerId)) return
      // A further finger, wherever it landed: it belongs to the gesture, and
      // nothing underneath may treat it as a tap of its own.
      e.stopPropagation()
      e.preventDefault()
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
      if (pointers.current.size === 2) startPinch()
    },
    [startPinch]
  )

  const detach = useRef<() => void>(() => {})

  const onUp = useCallback(
    (e: PointerEvent): void => {
      if (!pointers.current.has(e.pointerId)) return
      const wasPinch = pointers.current.size >= 2
      pointers.current.delete(e.pointerId)
      if (wasPinch) suppressClicksUntil.current = Date.now() + CLICK_SUPPRESS_MS

      if (pointers.current.size < 2) pinch.current = null
      if (pointers.current.size === 1) regrip()
      if (pointers.current.size === 0) {
        activeId.current = null
        grip.current = null
        detach.current()
      }
    },
    [regrip]
  )

  const attach = useCallback((): void => {
    if (listening.current) return
    listening.current = true
    const opts = { capture: true, passive: false } as const
    window.addEventListener('pointerdown', onDown, opts)
    window.addEventListener('pointermove', onMove, opts)
    window.addEventListener('pointerup', onUp, opts)
    window.addEventListener('pointercancel', onUp, opts)
    detach.current = (): void => {
      window.removeEventListener('pointerdown', onDown, opts)
      window.removeEventListener('pointermove', onMove, opts)
      window.removeEventListener('pointerup', onUp, opts)
      window.removeEventListener('pointercancel', onUp, opts)
      listening.current = false
    }
  }, [onDown, onMove, onUp])

  // A pinch finger's lift still produces a click on whatever is under it. Eat
  // it, so lifting a finger off the tray does not drop a new sticker.
  useEffect(() => {
    const swallow = (e: MouseEvent): void => {
      if (Date.now() < suppressClicksUntil.current) {
        e.stopPropagation()
        e.preventDefault()
      }
    }
    window.addEventListener('click', swallow, true)
    return (): void => {
      window.removeEventListener('click', swallow, true)
      detach.current()
    }
  }, [])

  const begin = useCallback(
    (e: React.PointerEvent, id: string): void => {
      e.preventDefault()
      e.stopPropagation()
      if (activeId.current && activeId.current !== id) {
        // Already holding another sticker: this finger joins that gesture.
        onDown(e.nativeEvent)
        return
      }
      if (!activeId.current) h.current.onGestureStart?.()
      activeId.current = id
      h.current.onSelect(id)
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
      if (pointers.current.size === 1) regrip()
      if (pointers.current.size === 2) startPinch()
      attach()
    },
    [attach, onDown, regrip, startPinch]
  )

  return { begin }
}
